import {
  Spotlight,
  type SpotlightActionData,
  type SpotlightActionGroupData,
} from "@mantine/spotlight";
import {
  IconFileOutline18,
  IconHashtagOutline18,
  IconMagnifierOutline18,
} from "nucleo-ui-outline-18";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@mountayaapp/locales";
import { ICON_SIZE } from "@mountayaapp/theme";

import { buildSearchRecords } from "../rules/searchIndex";

/**
 * RESULT_LIMIT caps the number of rendered actions so the palette stays fast and
 * keyboard-navigable no matter how many sections match.
 */
const RESULT_LIMIT = 12;

/**
 * DocsSearchProps carry the active language whose pages are indexed. It is
 * resolved from the URL by the header and passed in, matching how the rest of the
 * docs chrome receives the language.
 */
interface DocsSearchProps {
  language: SupportedLanguage;
}

/**
 * navigateToRecord performs a full-page navigation to a record's page (and its
 * section anchor when present), consistent with the docs app's no-client-router
 * architecture where every link is a full reload.
 */
const navigateToRecord = (href: string, anchor: string): void => {
  window.location.assign(anchor === "" ? href : `${href}#${anchor}`);
};

/**
 * DocsSearch is the Cmd+K / Ctrl+K command palette over the in-memory content
 * registry. It builds Spotlight actions from the search records, grouped by
 * navigation section, and navigates to the chosen page or section on selection.
 * The default Spotlight filter matches each action's label, description, and
 * keywords, so a query filters on the page title, the heading, and the section
 * text. The palette itself is a portal-backed overlay, so it mounts only after
 * hydration and renders nothing during the static prerender.
 */
export const DocsSearch = ({ language }: DocsSearchProps) => {
  const { t } = useTranslation("docs");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const actions = useMemo<SpotlightActionGroupData[]>(() => {
    const order: string[] = [];
    const byGroup = new Map<string, SpotlightActionData[]>();

    for (const record of buildSearchRecords(language)) {
      const groupLabel = t(record.group);
      let groupActions = byGroup.get(groupLabel);
      if (!groupActions) {
        groupActions = [];
        byGroup.set(groupLabel, groupActions);
        order.push(groupLabel);
      }

      const isSection = record.heading !== "";
      groupActions.push({
        description: isSection ? record.pageTitle : record.text,
        id: `${record.href}#${record.anchor}`,
        keywords: [record.pageTitle, record.heading, record.text].filter((value) => value !== ""),
        label: isSection ? record.heading : record.pageTitle,
        leftSection: isSection ? (
          <IconHashtagOutline18 size={ICON_SIZE.sm} />
        ) : (
          <IconFileOutline18 size={ICON_SIZE.sm} />
        ),
        onClick: () => {
          navigateToRecord(record.href, record.anchor);
        },
      });
    }

    return order.map((groupLabel) => ({
      actions: byGroup.get(groupLabel) ?? [],
      group: groupLabel,
    }));
  }, [language, t]);

  if (!mounted) {
    return null;
  }

  return (
    <Spotlight
      actions={actions}
      clearQueryOnClose
      highlightQuery
      limit={RESULT_LIMIT}
      nothingFound={t("search.nothingFound")}
      searchProps={{
        leftSection: <IconMagnifierOutline18 size={ICON_SIZE.sm} />,
        placeholder: t("search.placeholder"),
      }}
    />
  );
};
